import { IconX, IconPlus } from "@tabler/icons-react";
import React, { useState } from "react";
import SelectInput from "../../../components/common/SelectInput";

type Props = {
  open: boolean;
  onClose: () => void;
};

type WeightRangeData = {
  from: string;
  to: string;
  forClient: boolean;
  client: string;
};

const initialData: WeightRangeData = {
  from: "5",
  to: "10",
  forClient: true,
  client: "Client A",
};

const EditViewWeightModal = ({ open, onClose }: Props) => {
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState<WeightRangeData>(initialData);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleMode = () => {
    setEditMode(!editMode);
  };

  if (!open) return null;

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#F7F6F2] rounded-2xl w-[655px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#d2d2d2]">
          <h1 className="text-2xl">
            {editMode ? "Edit Weight Range" : "View Weight Range"}
          </h1>
          <button
            onClick={() => {
              if (editMode) setEditMode(false);
              else onClose();
            }}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="w-full max-h-[520px] overflow-y-auto p-4 divide-y divide-[#D2D2D2] space-y-4">
          {/* From Field */}
          <div className="flex justify-between items-center w-full p-1.5">
            <div className="w-full">
              <label className="text-sm font-medium text-[#212121]">From (kg)</label>
            </div>
            <div className="w-full">
              {editMode ? (
                <input
                  name="from"
                  value={data.from}
                  onChange={handleChange}
                  className="px-4 py-2 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8]"
                />
              ) : (
                <div className="text-[#1E293B]">{data.from}</div>
              )}
            </div>
          </div>

          {/* To Field with Plus */}
          <div className="flex justify-between items-center w-full p-1.5 gap-2">
            <div className="w-full">
              <label className="text-sm font-medium text-[#212121]">To (kg)</label>
            </div>
            <div className="w-full flex items-center gap-2">
              {editMode ? (
                <>
                  <input
                    name="to"
                    value={data.to}
                    onChange={handleChange}
                    className="px-4 py-2 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8]"
                  />
                  <button className="bg-[#FBD037]/20 border border-[#FBD037] text-white rounded-full p-2">
                    <IconPlus size={18} className="text-black" />
                  </button>
                </>
              ) : (
                <div className="text-[#1E293B]">{data.to}</div>
              )}
            </div>
          </div>

          {/* For Client Checkbox */}
          <div className="flex justify-between items-center w-full p-1.5">
            <div className="w-full">
              <label className="text-sm font-medium text-[#212121]">For Client</label>
            </div>
            <div className="w-full">
              {editMode ? (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="forClient"
                    name="forClient"
                    checked={data.forClient}
                    onChange={handleChange}
                    className="accent-[#FFCD4B] cursor-pointer"
                  />
                  <label htmlFor="forClient" className="text-sm text-[#1E293B] cursor-pointer">
                    For Client
                  </label>
                </div>
              ) : (
                <div className="text-[#1E293B]">
                  {data.forClient ? "Yes" : "No"}
                </div>
              )}
            </div>
          </div>

          {/* Client Select */}
          <div className="flex justify-between items-center w-full p-1.5">
            <div className="w-full">
              <label className="text-sm font-medium text-[#212121]">Client</label>
            </div>
            <div className="w-full">
              {editMode ? (
                <SelectInput
                  required
                  label="Select Client"
                  value={data.client}
                  selectOptions={[
                    { value: "Client A", text: "Client A" },
                    { value: "Client B", text: "Client B" },
                  ]}
                  setValue={(v: string) =>
                    setData((prev) => ({ ...prev, client: v }))
                  }
                />
              ) : (
                <div className="text-[#1E293B]">{data.client}</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2">
          <button
            className="border border-[#D8D8D8] bg-transparent flex items-center rounded-full p-2 px-4 text-base font-medium"
            onClick={() => {
              if (editMode) setEditMode(false);
              else onClose();
            }}
          >
            Cancel
          </button>
          <button
            className="border border-[#FBD037] bg-[#FBD037] flex items-center rounded-full p-2 px-6 text-sm font-medium"
            onClick={toggleMode}
          >
            {editMode ? "Update" : "Edit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditViewWeightModal;
